// Hook pour gérer le player vidéo HLS
import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { MediaService } from '../Services/MediaService';

export interface UseHLSPlayerOptions {
    autoPlay?: boolean;
    fallbackSrc?: string;
    preferredQuality?: 'low' | 'medium' | 'high' | 'auto';
    instanceId?: string; // ID de l'instance pour le cache
    componentId?: string; // ID du composant pour le cache
}

export interface UseHLSPlayerReturn {
    videoRef: React.RefObject<HTMLVideoElement>;
    isReady: boolean;
    error: Error | null;
    qualities: string[];
    currentQuality: number;
}

/**
 * Hook pour initialiser et gérer un player HLS
 */
export function useHLSPlayer(
    source: string | undefined,
    options: UseHLSPlayerOptions = {}
): UseHLSPlayerReturn {
    const {
        autoPlay = false,
        fallbackSrc,
        instanceId,
        componentId
    } = options;

    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [qualities, setQualities] = useState<string[]>([]);
    const [currentQuality, setCurrentQuality] = useState<number>(-1);

    useEffect(() => {
        if (!source || !videoRef.current) {
            return;
        }

        const videoElement = videoRef.current;
        let isMounted = true;

        const initializePlayer = async () => {
            try {
                // Si c'est un fichier local
                if (MediaService.isLocalFile(source)) {
                    videoElement.src = source;
                    
                    if (autoPlay) {
                        try {
                            await videoElement.play();
                        } catch (err) {
                            console.warn('Autoplay bloqué:', err);
                        }
                    }
                    
                    if (isMounted) {
                        setIsReady(true);
                    }
                    return;
                }

                // Obtenir l'URL HLS
                const hlsUrl = MediaService.getHLSUrl(source);
                
                // Extraire le token de l'URL pour l'utiliser dans les segments
                const hlsUrlObj = new URL(hlsUrl);
                const readToken = hlsUrlObj.searchParams.get('read_token') || '';

                // Le cache HLS fonctionne automatiquement via l'intercepteur fetch
                // Les segments .ts et manifests .m3u8 sont cachés automatiquement
                console.log('🎬 Initialisation HLS avec cache automatique');

                // Vérifier si HLS.js est supporté
                if (Hls.isSupported()) {
                    // Créer un loader personnalisé qui ajoute le token à toutes les URLs
                    class TokenLoader extends (Hls as any).DefaultConfig.loader {
                        load(context: any, config: any, callbacks: any) {
                            // Ajouter le token à l'URL si nécessaire
                            if (readToken && context.url) {
                                const separator = context.url.includes('?') ? '&' : '?';
                                if (!context.url.includes('read_token=')) {
                                    context.url = `${context.url}${separator}read_token=${readToken}`;
                                }
                            }
                            // Appeler le loader parent
                            return super.load(context, config, callbacks);
                        }
                    }

                    const hls = new Hls({
                        enableWorker: true,
                        lowLatencyMode: false,
                        backBufferLength: 90,
                        loader: TokenLoader,  // Utiliser notre loader personnalisé
                        // Configuration de la qualité adaptative intelligente
                        startLevel: -1,  // -1 = auto (détection automatique de la bande passante)
                        capLevelToPlayerSize: true,  // Limiter selon la taille du player
                        maxMaxBufferLength: 600,  // Buffer max 10 minutes
                        // Paramètres ABR (Adaptive Bitrate) optimisés
                        abrEwmaDefaultEstimate: 5000000,  // Estimation initiale : 5 Mbps
                        abrBandWidthFactor: 0.9,  // Utiliser 90% de la BP (plus conservateur)
                        abrBandWidthUpFactor: 0.8,  // Monte en qualité à 80% BP (plus prudent)
                        abrMaxWithRealBitrate: true,
                        // Limiter la qualité max pendant la lecture pour éviter segments trop longs
                        autoLevelCapping: -1  // -1 = pas de limite par défaut, géré dynamiquement
                    });

                    hlsRef.current = hls;

                    hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
                        if (!isMounted) return;

                        console.log('✅ HLS manifest chargé, qualités:', data.levels.length);
                        
                        const availableQualities = data.levels.map(
                            level => `${level.height}p (${Math.round(level.bitrate / 1000)}kbps)`
                        );
                        
                        setQualities(availableQualities);
                        setCurrentQuality(hls.currentLevel);
                        setIsReady(true);

                        console.log('🎬 Démarrage en qualité automatique (adaptera selon connexion)');

                        if (autoPlay) {
                            videoElement.play().catch(err => {
                                console.warn('Autoplay bloqué:', err);
                            });
                        }
                        
                        // Configuration intelligente : laisser HLS.js gérer le buffering
                        // On n'intervient PAS manuellement pour éviter de détruire le buffer
                        
                        console.log('✅ Mode adaptatif intelligent : HLS.js gère le buffering automatiquement');
                        console.log('💡 La qualité montera progressivement selon la bande passante disponible');
                    });

                    hls.on(Hls.Events.ERROR, (_, data) => {
                        console.error('Erreur HLS:', data);
                        
                        if (data.fatal) {
                            if (!isMounted) return;

                            setError(new Error(`Erreur HLS fatale: ${data.type}`));
                            
                            // Essayer le fallback
                            if (fallbackSrc) {
                                videoElement.src = fallbackSrc;
                                setIsReady(true);
                            }
                        }
                    });

                    hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
                        if (isMounted) {
                            setCurrentQuality(data.level);
                            const levelInfo = hls.levels[data.level];
                            console.log(`📊 Qualité changée: ${levelInfo.height}p (${Math.round(levelInfo.bitrate / 1000)}kbps)`);
                        }
                    });

                    hls.loadSource(hlsUrl);
                    hls.attachMedia(videoElement);

                } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    // Safari natif
                    videoElement.src = hlsUrl;
                    
                    videoElement.addEventListener('loadedmetadata', () => {
                        if (isMounted) {
                            setIsReady(true);
                            
                            if (autoPlay) {
                                videoElement.play().catch(err => {
                                    console.warn('Autoplay bloqué:', err);
                                });
                            }
                        }
                    });

                    videoElement.addEventListener('error', () => {
                        if (!isMounted) return;
                        
                        setError(new Error('Erreur de chargement vidéo'));
                        
                        if (fallbackSrc) {
                            videoElement.src = fallbackSrc;
                            setIsReady(true);
                        }
                    });
                } else {
                    setError(new Error('HLS non supporté'));
                    
                    if (fallbackSrc) {
                        videoElement.src = fallbackSrc;
                        setIsReady(true);
                    }
                }
            } catch (err) {
                if (isMounted) {
                    setError(err instanceof Error ? err : new Error('Erreur inconnue'));
                }
            }
        };

        initializePlayer();

        return () => {
            isMounted = false;
            
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [source, autoPlay, fallbackSrc]);

    return {
        videoRef,
        isReady,
        error,
        qualities,
        currentQuality
    };
}

